import { t } from '@hefe/i18n';
import {
  BasketballGameReportQuarter,
  ResponseBasketballGetGameInfo,
} from '@hefe/models';
import { EventReportsSlice } from '@hefe/store';
import { Button, Space } from 'antd';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../../../store/store';
import CustomForm, {
  FormField,
} from '../../../../core/components/form/custom-form';

export const GENERATE_EVENT_REPORT_WIZARD_QUARTERS_FORM_ID =
  'generate-event-report-wizard-quarters-form';

interface GenerateEventReportWizardQuartersFormComponentProps {
  data: ResponseBasketballGetGameInfo;
}

export default function GenerateEventReportWizardQuartersFormComponent(
  props: GenerateEventReportWizardQuartersFormComponentProps
) {
  const dispatch = useAppDispatch();
  const wizard = useSelector(EventReportsSlice.generateWizardSelector);

  const { data } = props;

  const [numberOfOvertimes, setNumberOfOvertimes] = useState(0);

  const onSubmit = (values: any, resetFields?: () => void) => {
    // store values in redux and go to next step
    const quarters: BasketballGameReportQuarter[] = [];
    for (let i = 1; i <= 4; i++) {
      if (
        !values[`scoreHomeQ${i}`] &&
        !values[`scoreGuestQ${i}`] &&
        !values[`specialEventsQ${i}`]
      ) {
        continue;
      }

      quarters.push({
        quarter: i,
        score_home: values[`scoreHomeQ${i}`],
        score_guest: values[`scoreGuestQ${i}`],
        special_events: values[`specialEventsQ${i}`],
      });
    }

    // Overtimes
    for (let i = 1; i <= numberOfOvertimes; i++) {
      if (
        !values[`scoreHomeOvertime${i}`] ||
        !values[`scoreGuestOvertime${i}`] ||
        !values[`specialEventsOvertime${i}`]
      ) {
        continue;
      }

      quarters.push({
        quarter: 4 + i,
        score_home: values[`scoreHomeOvertime${i}`],
        score_guest: values[`scoreGuestOvertime${i}`],
        special_events: values[`specialEventsOvertime${i}`],
      });
    }

    dispatch(
      EventReportsSlice.actions.setQuarters({
        score_home: values.finalScoreHome,
        score_guest: values.finalScoreGuest,
        quarters: quarters,
      })
    );
  };

  const getWizardQuarter = (quarter: number) =>
    wizard?.data?.quarters?.find((q) => q.quarter === quarter);

  const overTimeFields = useMemo(() => {
    const fields: FormField[] = [];
    for (let i = 1; i <= numberOfOvertimes; i++) {
      fields.push({
        label: `${t(
          'game_reports.generate.wizard.steps.game_details.overtime.title'
        )} ${i}`,
        name: `overtime${i}`,
        type: 'heading',
        rules: [],
      });
      fields.push({
        label: data?.game?.home ?? 'Team A',
        name: `scoreHomeOvertime${i}`,
        type: 'number',
        rules: [],
      });
      fields.push({
        label: data?.game?.guest ?? 'Team B',
        name: `scoreGuestOvertime${i}`,
        type: 'number',
        rules: [],
      });
      fields.push({
        label: t(
          'game_reports.generate.wizard.steps.game_details.special_events'
        ),
        name: `specialEventsOvertime${i}`,
        type: 'text',
        rules: [],
      });
    }
    return fields;
  }, [data?.game?.guest, data?.game?.home, numberOfOvertimes]);

  return (
    <div style={{ marginTop: 20, marginBottom: 20 }}>
      <Space>
        <Button
          onClick={() => setNumberOfOvertimes((oldValue) => oldValue + 1)}
        >
          {t('game_reports.generate.wizard.steps.game_details.overtime.add')}
        </Button>
        <Button
          onClick={() => setNumberOfOvertimes((oldValue) => oldValue - 1)}
          disabled={numberOfOvertimes === 0}
        >
          {t('game_reports.generate.wizard.steps.game_details.overtime.remove')}
        </Button>
      </Space>
      <CustomForm
        fields={[
          {
            label: t(
              'game_reports.generate.wizard.steps.game_details.final_score'
            ),
            name: 'header1',
            type: 'heading',
            rules: [],
          },
          {
            label: data?.game?.home ?? 'Team A',
            name: 'finalScoreHome',
            type: 'number',
            initalValue: wizard?.data?.score_home ?? data?.game?.homeScore,
            rules: [
              {
                required: true,
                message: t('form_errors.required'),
              },
            ],
          },
          {
            label: data?.game?.guest ?? 'Team B',
            name: 'finalScoreGuest',
            type: 'number',
            initalValue: wizard?.data?.score_guest ?? data?.game?.guestScore,
            rules: [
              {
                required: true,
                message: t('form_errors.required'),
              },
            ],
          },
          {
            label:
              t('game_reports.generate.wizard.steps.game_details.quarter') +
              ' 1',
            name: 'header1',
            type: 'heading',
            rules: [],
          },
          {
            label: data?.game?.home ?? 'Team A',
            name: 'scoreHomeQ1',
            type: 'number',
            initalValue:
              getWizardQuarter(1)?.score_home ?? data?.game?.homeScoreQ1,
            rules: [],
          },
          {
            label: data?.game?.guest ?? 'Team B',
            name: 'scoreGuestQ1',
            type: 'number',
            initalValue:
              getWizardQuarter(1)?.score_guest ?? data?.game?.guestScoreQ1,
            rules: [],
          },
          {
            label: t(
              'game_reports.generate.wizard.steps.game_details.special_events'
            ),
            name: 'specialEventsQ1',
            type: 'text',
            initalValue: getWizardQuarter(1)?.special_events,
            rules: [
              {
                max: 200,
                message: t('form_errors.max_length', { length: 200 }),
              },
            ],
          },
          {
            label:
              t('game_reports.generate.wizard.steps.game_details.quarter') +
              ' 2',
            name: 'header2',
            type: 'heading',
            rules: [],
          },
          {
            label: data?.game?.home ?? 'Team A',
            name: 'scoreHomeQ2',
            type: 'number',
            initalValue:
              getWizardQuarter(2)?.score_home ?? data?.game?.homeScoreQ2,
            rules: [],
          },
          {
            label: data?.game?.guest ?? 'Team B',
            name: 'scoreGuestQ2',
            type: 'number',
            initalValue:
              getWizardQuarter(2)?.score_guest ?? data?.game?.guestScoreQ2,
            rules: [],
          },
          {
            label: t(
              'game_reports.generate.wizard.steps.game_details.special_events'
            ),
            name: 'specialEventsQ2',
            type: 'text',
            initalValue: getWizardQuarter(2)?.special_events,
            rules: [
              {
                max: 200,
                message: t('form_errors.max_length', { length: 200 }),
              },
            ],
          },
          {
            label:
              t('game_reports.generate.wizard.steps.game_details.quarter') +
              ' 3',
            name: 'header3',
            type: 'heading',
            rules: [],
          },
          {
            label: data?.game?.home ?? 'Team A',
            name: 'scoreHomeQ3',
            type: 'number',
            initalValue:
              getWizardQuarter(3)?.score_home ?? data?.game?.homeScoreQ3,
            rules: [],
          },
          {
            label: data?.game?.guest ?? 'Team B',
            name: 'scoreGuestQ3',
            type: 'number',
            initalValue:
              getWizardQuarter(3)?.score_guest ?? data?.game?.guestScoreQ3,
            rules: [],
          },
          {
            label: t(
              'game_reports.generate.wizard.steps.game_details.special_events'
            ),
            name: 'specialEventsQ3',
            type: 'text',
            initalValue: getWizardQuarter(3)?.special_events,
            rules: [
              {
                max: 200,
                message: t('form_errors.max_length', { length: 200 }),
              },
            ],
          },
          {
            label:
              t('game_reports.generate.wizard.steps.game_details.quarter') +
              ' 4',
            name: 'header4',
            type: 'heading',
            rules: [],
          },
          {
            label: data?.game?.home ?? 'Team A',
            name: 'scoreHomeQ4',
            type: 'number',
            initalValue:
              getWizardQuarter(4)?.score_home ?? data?.game?.homeScoreQ4,
            rules: [],
          },
          {
            label: data?.game?.guest ?? 'Team B',
            name: 'scoreGuestQ4',
            type: 'number',
            initalValue:
              getWizardQuarter(4)?.score_guest ?? data?.game?.guestScoreQ4,
            rules: [],
          },
          {
            label: t(
              'game_reports.generate.wizard.steps.game_details.special_events'
            ),
            name: 'specialEventsQ4',
            type: 'text',
            initalValue: getWizardQuarter(4)?.special_events,
            rules: [
              {
                max: 200,
                message: t('form_errors.max_length', { length: 200 }),
              },
            ],
          },
          ...overTimeFields,
        ]}
        onSubmit={onSubmit}
        formId={GENERATE_EVENT_REPORT_WIZARD_QUARTERS_FORM_ID}
      />
    </div>
  );
}
